import { NavLink, useParams } from 'react-router-dom';
import { Icon, type IconName } from '@/lib/icons';
import { useApp } from '@/hooks/use-app-context';
import { normalizeUserRole } from '@/lib/auth/roles';
import { cn } from '@/lib/utils';

type Entry = { path: string; label: string; icon: IconName; contributorOnly?: boolean };

/** The four sub-modules of "Données initiales", so they read as one module. */
export function InitialDataSubNav({ className }: { className?: string }) {
  const { t, user } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const copy = t.initialDataReview.subNav;
  const base = `/${lang || 'fr'}`;
  const isAccountant = normalizeUserRole(user?.role) === 'accountant';

  const entries: Entry[] = [
    { path: 'initial-audio', label: copy.audio, icon: 'mic' },
    { path: 'initial-text', label: copy.text, icon: 'fileText' },
    { path: 'initial-transcription', label: copy.transcription, icon: 'languages', contributorOnly: true },
    { path: 'initial-review', label: copy.review, icon: 'checkCircle', contributorOnly: true },
  ];

  return (
    <nav aria-label={copy.label} className={cn('mx-auto w-full max-w-5xl px-4 pt-5 sm:px-6 sm:pt-6', className)} data-testid="initial-data-subnav">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-xs font-medium uppercase tracking-[0.08em] text-faint-foreground">{copy.label}</span>
        {entries
          .filter((entry) => !(entry.contributorOnly && isAccountant))
          .map((entry) => (
            <NavLink
              key={entry.path}
              to={`${base}/${entry.path}`}
              className={({ isActive }) =>
                cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'border-brand/40 bg-brand/10 font-medium text-brand'
                    : 'border-border/60 bg-surface text-muted-foreground hover:border-brand/40 hover:text-foreground',
                )
              }
            >
              <Icon name={entry.icon} className="size-4" />
              {entry.label}
            </NavLink>
          ))}
      </div>
    </nav>
  );
}
